import { useCallback, useEffect, useState } from 'react'
import {
  type ResolvedTheme,
  type ThemeChoice,
  applyTheme,
  readThemeChoice,
  resolveTheme,
  storeThemeChoice,
  systemPrefersDark,
  watchSystemTheme,
} from '../lib/theme'

/**
 * The theme choice, and the document kept in step with it.
 *
 * The attribute is already on `<html>` before React runs — the inline script
 * in index.html put it there, which is what keeps the first paint from
 * flashing the wrong theme. This hook does not re-derive it on mount; it
 * takes over from there, so a re-render never fights the script.
 *
 * The OS listener is subscribed only while the choice is 'system'. Listening
 * unconditionally and filtering inside would still run on every OS change
 * for someone who has chosen explicitly, and would keep a listener alive for
 * an event that can never matter to them.
 */
export function useTheme() {
  const [choice, setChoice] = useState<ThemeChoice>(() => readThemeChoice())
  const [prefersDark, setPrefersDark] = useState<boolean>(() => systemPrefersDark())

  const resolved: ResolvedTheme = resolveTheme(choice, prefersDark)

  useEffect(() => {
    applyTheme(resolved)
  }, [resolved])

  useEffect(() => {
    if (choice !== 'system') return
    return watchSystemTheme(setPrefersDark)
  }, [choice])

  const setTheme = useCallback((next: ThemeChoice) => {
    setChoice(next)
    storeThemeChoice(next)
  }, [])

  return { choice, resolved, setTheme }
}
